import React, { type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Button,
  Flex,
  GRID_VARIANT_SMALL,
  GRID_VARIANT_VERTICAL_8,
  Grid,
  GridCell,
  Loading,
  ModalBody,
  ModalFoot,
} from 'mailup-web-components'

import RenderWhen from 'Components/RenderWhen'

import InputField from 'Pages/RecipientsList/components/modals/bulkFindReplace/InputField'
import InputFind from 'Pages/RecipientsList/components/modals/bulkFindReplace/InputFind'
import InputMode from 'Pages/RecipientsList/components/modals/bulkFindReplace/InputMode'
import InputReplace from 'Pages/RecipientsList/components/modals/bulkFindReplace/InputReplace'
import PreviewNoResults from 'Pages/RecipientsList/components/modals/bulkFindReplace/PreviewNoResults'
import PreviewResults from 'Pages/RecipientsList/components/modals/bulkFindReplace/PreviewResults'
import Spacing from 'Pages/RecipientsList/components/modals/bulkFindReplace/Spacing'

import {
  modalBulkFindReplaceCancelCallback,
  modalBulkFindReplaceConfirmCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalBulkFindReplace'
import {
  modalBulkFindReplaceCanReplaceState,
  modalBulkFindReplaceHasNoResultsState,
  modalBulkFindReplaceHasResultsState,
  modalBulkFindReplaceState,
} from 'Pages/RecipientsList/recoil/state/modalBulkFindReplace'

import useModalBulkFindReplacePreview from 'Pages/RecipientsList/hooks/useModalBulkFindReplacePreview'

const TabFindReplace: FC = () => {
  const intl = useIntl()

  useModalBulkFindReplacePreview()

  const { totalItems, previewStatus } = useRecoilValue(
    modalBulkFindReplaceState,
  )
  const canReplace = useRecoilValue(modalBulkFindReplaceCanReplaceState)

  const hasNoResults = useRecoilValue(modalBulkFindReplaceHasNoResultsState)
  const hasResults = useRecoilValue(modalBulkFindReplaceHasResultsState)

  const handleCancel = useRecoilCallback(modalBulkFindReplaceCancelCallback, [])

  const handleConfirm = useRecoilCallback(
    modalBulkFindReplaceConfirmCallback,
    [],
  )
  return (
    <>
      <Loading open={previewStatus === 'fetching'} />
      <ModalBody scrollable scrollableHeight="500px">
        <Flex flexDirection="column" gap="24px" margin="24px 0 0 0">
          <Grid
            cols={3}
            variant={GRID_VARIANT_SMALL}
            variantVertical={GRID_VARIANT_VERTICAL_8}
          >
            <GridCell span={2}>
              <InputField />
            </GridCell>
            <GridCell span={1} />
            <GridCell span={2}>
              <InputFind />
            </GridCell>
            <GridCell span={1}>
              <InputMode />
            </GridCell>
          </Grid>

          <Grid cols={3} variant={GRID_VARIANT_SMALL}>
            <GridCell span={2}>
              <InputReplace />
            </GridCell>
            <GridCell span={1} />
          </Grid>

          <RenderWhen condition={!hasNoResults && !hasResults}>
            <Spacing />
          </RenderWhen>

          <RenderWhen condition={hasNoResults}>
            <PreviewNoResults />
          </RenderWhen>

          <RenderWhen condition={hasResults}>
            <PreviewResults />
          </RenderWhen>
        </Flex>
      </ModalBody>
      <ModalFoot>
        <Button
          kind={BUTTON_VARIANT_SECONDARY}
          data-mu-test="cancel"
          onClick={handleCancel}
        >
          {intl.formatMessage({ id: 'Load.MailupCommon.Cancel' })}
        </Button>

        <Button
          kind={BUTTON_VARIANT_PRIMARY}
          data-mu-test="confirm"
          onClick={handleConfirm}
          disabled={!canReplace}
        >
          {intl.formatMessage(
            {
              id: 'Load.RecipientsList.ModalBulkFindReplaceConfirm',
            },
            { N: totalItems },
          )}
        </Button>
      </ModalFoot>
    </>
  )
}

export default TabFindReplace
